import React from "react"
import { graphql } from "gatsby"
import Helmet from "react-helmet"
import { GatsbyImage } from "gatsby-plugin-image"
import Layout from "../components/layout"
import Seo from "../components/seo"
import NewsletterCta from "../components/newsletter-cta"
import Typography from "@material-ui/core/Typography"
import Container from "@material-ui/core/Container"
import Box from "@material-ui/core/Box"
import Paper from "@material-ui/core/Paper"
import Button from "@material-ui/core/Button"
import Grid from "@material-ui/core/Grid"
import CheckIcon from "@material-ui/icons/Check"
import { makeStyles } from "@material-ui/core/styles"
import ebooks from "../data/ebooks"
import { trackEvent } from "../lib/analytics"
import {
  ebookPath,
  isPurchasable,
  currentPrice,
  formatPrice,
} from "../lib/ebook"

const useStyles = makeStyles((theme) => ({
  header: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(4),
  },
  eyebrow: {
    color: theme.palette.primary.dark,
    letterSpacing: "0.1em",
  },
  section: {
    marginBottom: theme.spacing(6),
  },
  list: {
    listStyle: "none",
    paddingLeft: 0,
  },
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.5),
  },
  checkIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  buyBox: {
    padding: theme.spacing(4, 4),
    textAlign: "center",
  },
  fullPrice: {
    textDecoration: "line-through",
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  faqQuestion: {
    marginTop: 0,
  },
  bonbonName: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
}))

const statusLine = (ebook) => {
  switch (ebook.status) {
    case "presale":
      return `Early bird pre-order · delivered ${ebook.presaleDelivery}`
    case "available":
      return "Available now · instant PDF download"
    default:
      return `Coming ${ebook.launch}`
  }
}

const BuyBox = ({ ebook }) => {
  const classes = useStyles()
  const price = currentPrice(ebook)
  const discounted = price !== ebook.price

  return (
    <Paper className={classes.buyBox}>
      <Typography variant="h3" component="p" gutterBottom>
        {discounted && (
          <span className={classes.fullPrice}>
            {formatPrice(ebook.price, ebook.currency)}
          </span>
        )}
        {formatPrice(price, ebook.currency)}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {statusLine(ebook)}
      </Typography>
      <Box mt={3}>
        <Button
          variant="contained"
          size="large"
          color="primary"
          fullWidth
          href={ebook.checkoutUrl}
          onClick={() =>
            trackEvent("begin_checkout", {
              currency: ebook.currency,
              value: price,
              items: [{ item_id: ebook.slug, item_name: ebook.title, price }],
            })
          }
        >
          {ebook.status === "presale" ? "Pre-order now" : "Buy now"}
        </Button>
      </Box>
      <Box mt={2}>
        <Typography variant="caption" display="block">
          Secure checkout. Prices include any applicable taxes.
        </Typography>
      </Box>
    </Paper>
  )
}

const WaitlistBox = ({ ebook, fieldId }) => (
  <NewsletterCta
    fieldId={fieldId}
    heading="Join the waitlist"
    body={`Be the first to know when ${ebook.title} launches ${ebook.launch}, with an early bird price of ${formatPrice(
      ebook.earlyBirdPrice,
      ebook.currency
    )} (instead of ${formatPrice(
      ebook.price,
      ebook.currency
    )}) for the first 48 hours.`}
    buttonLabel="Join the waitlist"
    successLabel="You're on the list"
    listFields={ebook.mailchimpFields}
    onSubscribe={() =>
      trackEvent("generate_lead", { ebook: ebook.slug, source: "ebook_page" })
    }
  />
)

const EbookTemplate = ({ data, pageContext }) => {
  const classes = useStyles()
  const ebook = ebooks.find((e) => e.slug === pageContext.slug)
  const purchasable = isPurchasable(ebook)
  const description = `${ebook.subtitle}. ${ebook.pitch[0]}`
  const imageFor = (path) =>
    data.images.nodes.find((node) => node.relativePath === path)
  const hero = imageFor(ebook.heroImage)
  const heroUrl = hero && `https://dessertisans.com${hero.publicURL}`

  const productJSONLD = {
    "@context": "http://schema.org",
    "@type": "Product",
    name: ebook.title,
    description,
    image: heroUrl,
    offers: {
      "@type": "Offer",
      price: currentPrice(ebook),
      priceCurrency: ebook.currency,
      availability:
        ebook.status === "presale"
          ? "http://schema.org/PreOrder"
          : "http://schema.org/InStock",
      url: `https://dessertisans.com${ebookPath(ebook)}`,
    },
  }

  const callToAction = (position) =>
    purchasable ? (
      <BuyBox ebook={ebook} />
    ) : (
      <WaitlistBox ebook={ebook} fieldId={`waitlist-email-${position}`} />
    )

  return (
    <Layout>
      <Seo
        title={`${ebook.title} ebook`}
        description={description}
        pathname={ebookPath(ebook)}
        image={heroUrl}
      />
      {purchasable && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(productJSONLD)}
          </script>
        </Helmet>
      )}
      <div style={{ background: "#fff" }}>
        <Container maxWidth="sm" className={classes.header}>
          <Typography
            variant="overline"
            component="p"
            align="center"
            className={classes.eyebrow}
          >
            Dessertisans ebook · {statusLine(ebook)}
          </Typography>
          <Typography variant="h1" component="h1" gutterBottom align="center">
            {ebook.title}
          </Typography>
          <Typography
            variant="subtitle1"
            component="h2"
            gutterBottom
            align="center"
          >
            {ebook.subtitle}
          </Typography>
        </Container>

        {hero && (
          <Container maxWidth="md">
            <Box mb={6}>
              <GatsbyImage
                image={hero.childImageSharp.gatsbyImageData}
                alt={`The ${ebook.title} box`}
                loading="eager"
              />
            </Box>
          </Container>
        )}

        <Container maxWidth="sm">
          <Box className={classes.section}>
            {ebook.pitch.map((paragraph) => (
              <Typography key={paragraph} variant="body1" paragraph>
                {paragraph}
              </Typography>
            ))}
          </Box>

          <Box className={classes.section}>{callToAction("top")}</Box>
        </Container>

        {(ebook.bonbons || []).length > 0 && (
          <Container maxWidth="md" className={classes.section}>
            <Typography variant="h3" gutterBottom align="center">
              The {ebook.bonbons.length} bonbons
            </Typography>
            <Grid container spacing={4}>
              {ebook.bonbons.map((bonbon) => {
                const image = imageFor(bonbon.image)
                return (
                  <Grid key={bonbon.name} item xs={12} sm={6} md={4}>
                    {image && (
                      <GatsbyImage
                        image={image.childImageSharp.gatsbyImageData}
                        alt={bonbon.name}
                      />
                    )}
                    <Typography
                      variant="h4"
                      component="h4"
                      className={classes.bonbonName}
                    >
                      {bonbon.name}
                    </Typography>
                    <Typography variant="body2">{bonbon.description}</Typography>
                  </Grid>
                )
              })}
            </Grid>
          </Container>
        )}

        <Container maxWidth="sm">

          <Box className={classes.section}>
            <Typography variant="h3" gutterBottom>
              What you get
            </Typography>
            <ul className={classes.list}>
              {ebook.highlights.map((highlight) => (
                <li key={highlight} className={classes.listItem}>
                  <CheckIcon className={classes.checkIcon} />
                  <Typography variant="body1" component="span">
                    {highlight}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h3" gutterBottom>
              Inside the book
            </Typography>
            <ol>
              {ebook.contents.map((chapter) => (
                <li key={chapter}>
                  <Typography variant="body1" component="span">
                    {chapter}
                  </Typography>
                </li>
              ))}
            </ol>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h3" gutterBottom>
              Questions
            </Typography>
            {ebook.faq.map(({ question, answer }) => (
              <Box key={question} mb={3}>
                <Typography
                  variant="h5"
                  component="h4"
                  gutterBottom
                  className={classes.faqQuestion}
                >
                  {question}
                </Typography>
                <Typography variant="body1">{answer}</Typography>
              </Box>
            ))}
          </Box>

          <Box className={classes.section}>{callToAction("bottom")}</Box>
        </Container>
      </div>
    </Layout>
  )
}

export default EbookTemplate

export const pageQuery = graphql`
  query EbookImages($images: [String]!) {
    images: allFile(filter: { relativePath: { in: $images } }) {
      nodes {
        relativePath
        publicURL
        childImageSharp {
          gatsbyImageData(width: 960, placeholder: BLURRED)
        }
      }
    }
  }
`
